(function () {
  const selector = '[data-pdp-anchor-nav]';
  const headerSelector = '.section-header';

  function getSectionId(root) {
    return root?.dataset.sectionId;
  }

  function getProductId(root) {
    return root?.dataset.productId;
  }

  function getHeaderElement() {
    return document.querySelector(headerSelector);
  }

  function getHeaderOffset() {
    const header = getHeaderElement();
    if (!header) return 0;

    const rect = header.getBoundingClientRect();
    return Math.max(0, Math.min(rect.height, rect.bottom));
  }

  function getProductInfo(root) {
    const productId = getProductId(root);
    if (!productId) return null;
    return document.querySelector(`product-info[data-product-id="${productId}"]`);
  }

  function getRealSubmitButton(root) {
    return getProductInfo(root)?.querySelector('.product-form__submit');
  }

  function getCurrentPriceSource(root) {
    return getProductInfo(root)?.querySelector('[data-main-product-price]');
  }

  function getDesktopNav(root) {
    return root.querySelector('.pdp-anchor-nav__nav');
  }

  function getDesktopLinks(root) {
    return Array.from(getDesktopNav(root)?.querySelectorAll('[data-anchor-link]') || []);
  }

  function updateBarHeight(root) {
    const mobileBar = root.querySelector('.pdp-anchor-nav__mobile-bar');
    if (!mobileBar) return;
    root.style.setProperty('--pdp-anchor-nav-bar-height', `${mobileBar.offsetHeight}px`);
  }

  function updatePanelPosition(root) {
    const rect = root.getBoundingClientRect();
    root.style.setProperty('--pdp-anchor-nav-panel-top', `${Math.max(0, rect.bottom)}px`);
  }

  function ensurePlaceholder(root) {
    if (root._anchorPlaceholder?.isConnected) return root._anchorPlaceholder;

    const placeholder = document.createElement('div');
    placeholder.className = 'pdp-anchor-nav__placeholder';
    root.parentNode.insertBefore(placeholder, root);
    root._anchorPlaceholder = placeholder;
    return placeholder;
  }

  function syncPlaceholder(root) {
    const placeholder = ensurePlaceholder(root);
    const isFixed = root.classList.contains('is-fixed');

    placeholder.classList.toggle('is-active', isFixed);
    placeholder.style.height = isFixed ? `${root.offsetHeight}px` : '0px';
  }

  function updateStickyState(root) {
    const placeholder = ensurePlaceholder(root);
    const anchorRect = placeholder.classList.contains('is-active')
      ? placeholder.getBoundingClientRect()
      : root.getBoundingClientRect();
    const headerOffset = getHeaderOffset();
    const shouldFix = anchorRect.top <= headerOffset;

    root.style.setProperty('--pdp-anchor-nav-offset', `${headerOffset}px`);
    root.classList.toggle('is-fixed', shouldFix);
    syncPlaceholder(root);
    updatePanelPosition(root);
  }

  function updatePrice(root, source) {
    const priceSource = source || getCurrentPriceSource(root);
    if (!priceSource) return;

    const currentText = priceSource.querySelector('.main-product-price-custom__current')?.textContent?.trim();
    const compareText = priceSource.querySelector('.main-product-price-custom__compare')?.textContent?.trim();
    const currentPrice = root.querySelector('[data-anchor-current-price]');
    const comparePrice = root.querySelector('[data-anchor-compare-price]');

    if (currentPrice && currentText) currentPrice.textContent = currentText;

    if (comparePrice) {
      if (compareText) {
        comparePrice.hidden = false;
        comparePrice.textContent = compareText;
      } else {
        comparePrice.hidden = true;
        comparePrice.textContent = '';
      }
    }
  }

  function syncSubmitButtons(root) {
    const realButton = getRealSubmitButton(root);
    const defaultLabel = root.dataset.defaultButtonLabel || 'Add to Cart';
    const text = realButton?.querySelector('span')?.textContent?.trim() || defaultLabel;
    const disabled =
      !realButton ||
      realButton.hasAttribute('disabled') ||
      realButton.getAttribute('aria-disabled') === 'true';
    const isLoading = !!realButton?.classList.contains('loading');

    root.querySelectorAll('[data-anchor-add-to-cart]').forEach((button) => {
      button.disabled = disabled;
      button.classList.toggle('is-loading', isLoading);
    });

    root.querySelectorAll('[data-anchor-add-to-cart-text]').forEach((label) => {
      label.textContent = text || defaultLabel;
    });
  }

  function setActiveIndex(root, index, options = {}) {
    const nextIndex = String(index);
    const activeLink = root.querySelector(`[data-anchor-link][data-anchor-index="${nextIndex}"]`);
    const activeLabel = activeLink?.textContent?.trim();

    root.querySelectorAll('[data-anchor-link]').forEach((link) => {
      link.classList.toggle('is-active', link.dataset.anchorIndex === nextIndex);
    });

    const currentLabel = root.querySelector('[data-anchor-current-label]');
    if (currentLabel && activeLabel) currentLabel.textContent = activeLabel;
    root.dataset.activeIndex = nextIndex;
    adjustDesktopNavVisibility(root, nextIndex, options);
  }

  function adjustDesktopNavVisibility(root, index, options = {}) {
    if (window.innerWidth < 750) return;

    const nav = getDesktopNav(root);
    if (!nav) return;

    const links = getDesktopLinks(root);
    const activeIndex = links.findIndex((link) => link.dataset.anchorIndex === String(index));
    if (activeIndex === -1) return;

    const activeLink = links[activeIndex];
    const maxScrollLeft = Math.max(0, nav.scrollWidth - nav.clientWidth);

    if (maxScrollLeft <= 0) return;

    const gap = parseFloat(window.getComputedStyle(nav).columnGap || window.getComputedStyle(nav).gap || '0') || 0;
    const behavior = options.immediate ? 'auto' : 'smooth';
    const tolerance = 2;
    const visibleLeft = nav.scrollLeft;
    const visibleRight = visibleLeft + nav.clientWidth;
    const activeLeft = activeLink.offsetLeft;
    const activeRight = activeLeft + activeLink.offsetWidth;
    const intersectingLinks = links.filter((link) => {
      const linkLeft = link.offsetLeft;
      const linkRight = linkLeft + link.offsetWidth;
      return linkRight > visibleLeft + tolerance && linkLeft < visibleRight - tolerance;
    });
    const firstIntersectingLink = intersectingLinks[0];
    const lastIntersectingLink = intersectingLinks[intersectingLinks.length - 1];
    const firstIntersectingIndex = firstIntersectingLink ? links.indexOf(firstIntersectingLink) : -1;
    const lastIntersectingIndex = lastIntersectingLink ? links.indexOf(lastIntersectingLink) : -1;
    const hiddenLeftByViewport = firstIntersectingIndex > 0;
    const hiddenRightByViewport = lastIntersectingIndex > -1 && lastIntersectingIndex < links.length - 1;

    if (hiddenLeftByViewport && activeIndex === firstIntersectingIndex) {
      const previousLink = links[firstIntersectingIndex - 1];
      nav.scrollTo({
        left: Math.max(0, previousLink.offsetLeft - gap),
        behavior,
      });
      return;
    }

    if (hiddenRightByViewport && activeIndex === lastIntersectingIndex) {
      const nextLink = links[lastIntersectingIndex + 1];
      if (nextLink) {
        const nextRight = nextLink.offsetLeft + nextLink.offsetWidth;
        nav.scrollTo({
          left: Math.min(maxScrollLeft, nextRight - nav.clientWidth + gap),
          behavior,
        });
        return;
      }
    }

    if (activeLeft < visibleLeft - tolerance) {
      nav.scrollTo({
        left: Math.max(0, activeLeft - gap),
        behavior,
      });
      return;
    }

    if (activeRight > visibleRight + tolerance) {
      nav.scrollTo({
        left: Math.min(maxScrollLeft, activeRight - nav.clientWidth + gap),
        behavior,
      });
      return;
    }

    const fullyVisibleLinks = links.filter((link) => {
      const linkLeft = link.offsetLeft;
      const linkRight = linkLeft + link.offsetWidth;
      return linkLeft >= visibleLeft - tolerance && linkRight <= visibleRight + tolerance;
    });

    const firstVisibleLink = fullyVisibleLinks[0];
    const lastVisibleLink = fullyVisibleLinks[fullyVisibleLinks.length - 1];
    const firstVisibleIndex = firstVisibleLink ? links.indexOf(firstVisibleLink) : -1;
    const lastVisibleIndex = lastVisibleLink ? links.indexOf(lastVisibleLink) : -1;
    const hasHiddenLeft = firstVisibleIndex > 0;
    const hasHiddenRight = lastVisibleIndex > -1 && lastVisibleIndex < links.length - 1;

    if (hasHiddenLeft && activeIndex === firstVisibleIndex) {
      const previousLink = links[firstVisibleIndex - 1];
      nav.scrollTo({
        left: Math.max(0, previousLink.offsetLeft - gap),
        behavior,
      });
      return;
    }

    if (!hasHiddenRight || activeIndex !== lastVisibleIndex) return;

    const nextLink = links[lastVisibleIndex + 1];
    if (!nextLink) return;

    const nextRight = nextLink.offsetLeft + nextLink.offsetWidth;

    nav.scrollTo({
      left: Math.min(maxScrollLeft, nextRight - nav.clientWidth + gap),
      behavior,
    });
  }

  function closeMobilePanel(root) {
    const panel = root.querySelector('[data-anchor-mobile-panel]');
    const trigger = root.querySelector('[data-anchor-mobile-trigger]');
    if (!panel) return;

    panel.hidden = true;
    root.classList.remove('is-mobile-open');
    document.documentElement.classList.remove('pdp-anchor-nav-lock');
    document.body.classList.remove('pdp-anchor-nav-lock');

    if (trigger) trigger.setAttribute('aria-expanded', 'false');
  }

  function openMobilePanel(root) {
    const panel = root.querySelector('[data-anchor-mobile-panel]');
    const trigger = root.querySelector('[data-anchor-mobile-trigger]');
    if (!panel) return;

    updateBarHeight(root);
    panel.hidden = false;
    root.classList.add('is-mobile-open');
    document.documentElement.classList.add('pdp-anchor-nav-lock');
    document.body.classList.add('pdp-anchor-nav-lock');

    if (trigger) trigger.setAttribute('aria-expanded', 'true');
  }

  function scrollToTarget(root, href) {
    if (!href || href.charAt(0) !== '#') return false;

    const target = document.querySelector(href);
    if (!target) return false;

    const headerHeight = getHeaderOffset();
    const barHeight = root.offsetHeight || 0;
    const top = target.getBoundingClientRect().top + window.scrollY - headerHeight - barHeight - 12;

    window.scrollTo({
      top: Math.max(0, top),
      behavior: 'smooth',
    });

    return true;
  }

  function handleAnchorClick(root, link, event) {
    const href = link.getAttribute('href');
    setActiveIndex(root, link.dataset.anchorIndex);
    closeMobilePanel(root);

    if (scrollToTarget(root, href)) {
      event.preventDefault();
    }
  }

  function bindEvents(root) {
    root.querySelectorAll('[data-anchor-link]').forEach((link) => {
      if (link.dataset.anchorBound === 'true') return;
      link.dataset.anchorBound = 'true';

      link.addEventListener('click', function (event) {
        handleAnchorClick(root, this, event);
      });
    });

    root.querySelectorAll('[data-anchor-add-to-cart]').forEach((button) => {
      if (button.dataset.anchorSubmitBound === 'true') return;
      button.dataset.anchorSubmitBound = 'true';

      button.addEventListener('click', function () {
        const realButton = getRealSubmitButton(root);
        if (!realButton || realButton.disabled || realButton.getAttribute('aria-disabled') === 'true') return;
        realButton.click();
      });
    });

    const mobileTrigger = root.querySelector('[data-anchor-mobile-trigger]');
    if (mobileTrigger && mobileTrigger.dataset.anchorToggleBound !== 'true') {
      mobileTrigger.dataset.anchorToggleBound = 'true';
      mobileTrigger.addEventListener('click', function () {
        const panel = root.querySelector('[data-anchor-mobile-panel]');
        if (!panel || panel.hidden) {
          openMobilePanel(root);
        } else {
          closeMobilePanel(root);
        }
      });
    }

    root.querySelectorAll('[data-anchor-mobile-close]').forEach((button) => {
      if (button.dataset.anchorCloseBound === 'true') return;
      button.dataset.anchorCloseBound = 'true';
      button.addEventListener('click', function () {
        closeMobilePanel(root);
      });
    });

    if (!root.dataset.anchorResizeBound) {
      root.dataset.anchorResizeBound = 'true';
      window.addEventListener('resize', function () {
        updateBarHeight(root);
        updateStickyState(root);
        if (window.innerWidth >= 750) closeMobilePanel(root);
      });
    }

    if (!root.dataset.anchorEscBound) {
      root.dataset.anchorEscBound = 'true';
      document.addEventListener('keydown', function (event) {
        if (event.key === 'Escape') closeMobilePanel(root);
      });
    }
  }

  function observeSubmitButton(root) {
    const realButton = getRealSubmitButton(root);
    if (!realButton || root._anchorSubmitObserver) return;

    root._anchorSubmitObserver = new MutationObserver(() => {
      syncSubmitButtons(root);
    });

    root._anchorSubmitObserver.observe(realButton, {
      attributes: true,
      attributeFilter: ['class', 'disabled', 'aria-disabled'],
      childList: true,
      subtree: true,
    });
  }

  function initRoot(root) {
    if (!root) return;

    updateBarHeight(root);
    updatePanelPosition(root);
    syncPlaceholder(root);
    updateStickyState(root);
    bindEvents(root);
    observeSubmitButton(root);
    updatePrice(root);
    syncSubmitButtons(root);

    const hash = window.location.hash;
    const hashMatch = hash ? root.querySelector(`[data-anchor-link][href="${hash}"]`) : null;
    const initialIndex = hashMatch?.dataset.anchorIndex || root.dataset.activeIndex || root.dataset.initialIndex;
    if (initialIndex) {
      setActiveIndex(root, initialIndex, { immediate: true });
    }
  }

  function initAll(scope) {
    (scope || document).querySelectorAll(selector).forEach(initRoot);
  }

  function updateAllStickyStates() {
    document.querySelectorAll(selector).forEach((root) => {
      updateBarHeight(root);
      updateStickyState(root);
    });
  }

  function syncFromVariantChange(event) {
    const sectionId = event?.data?.sectionId;
    if (!sectionId) return;

    document.querySelectorAll(selector).forEach((root) => {
      const productInfo = getProductInfo(root);
      if (!productInfo || productInfo.dataset.section !== sectionId) return;

      const priceSource = event?.data?.html?.querySelector(`#price-${sectionId} [data-main-product-price]`);
      updatePrice(root, priceSource);
      window.requestAnimationFrame(() => {
        syncSubmitButtons(root);
        updateStickyState(root);
      });
    });
  }

  function initModule() {
    initAll(document);

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && !window.__pdpAnchorNavSubscribed) {
      window.__pdpAnchorNavSubscribed = true;
      subscribe(PUB_SUB_EVENTS.variantChange, syncFromVariantChange);
    }

    if (!window.__pdpAnchorNavLoadedBound) {
      window.__pdpAnchorNavLoadedBound = true;
      document.addEventListener('product-info:loaded', function (event) {
        initAll(event.target);
      });
    }

    if (!window.__pdpAnchorNavScrollBound) {
      window.__pdpAnchorNavScrollBound = true;

      let ticking = false;
      const requestUpdate = () => {
        if (ticking) return;
        ticking = true;
        window.requestAnimationFrame(() => {
          updateAllStickyStates();
          ticking = false;
        });
      };

      window.addEventListener('scroll', requestUpdate, { passive: true });
      window.addEventListener('load', requestUpdate);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initModule);
  } else {
    initModule();
  }
})();
